import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { GridText } from "src/components/Grid/GridText";
import {
    extendColumnsByScores,
    extendColumnsByJsonAttributes,
    extendByColumns,
} from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { GridProduct } from "src/api/client-api/products/useGetAllproduct";
import { GridLink } from "src/components/Grid/GridLink";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useGetAllProductLines } from "src/api/client-api/productLines/useProductLines";
import { isBrandCollectionError, useGetAllBrands } from "src/api/client-api/brand/useGetAllBrand";
import { StringFilter } from "src/components/Grid/filters/StringFilter";
import { SelectManyFromFilter } from "src/components/Grid/filters/SelectManyFromFilter/SelectManyFromFilter";
import { useTagsColumn } from "src/components/Grid/hooks/tags/useTagsColumn";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { ScoreFilter } from "src/components/Grid/filters/ScoreFilter";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

type ProductColumnsOptions = {
    brandExistenceQuery: ReturnType<typeof useGetAllBrands>;
    brandFilterQuery: ReturnType<typeof useGetAllBrands>;
    gridKey: string;
    data?: GridProduct[];
};
export const useProductColumns = (options: ProductColumnsOptions): MRT_ColumnDef<GridProduct, any>[] => {
    const { brandExistenceQuery, brandFilterQuery, data, gridKey } = options;

    const { t } = useTranslation();
    const isProductUxV2 = useIsProductUxV2();
    const appearance = isProductUxV2 ? "product-v2" : "legacy";
    const navigate = useNavigate();
    const location = useLocation();

    const { company } = useCompanyMetadata();
    const { tagColumns } = useTagsColumn<GridProduct>({
        appearance: isProductUxV2 ? "product-v2" : "legacy",
        gridKey,
        context: "product",
    });
    const {
        productLines = [],
        productLinesHierarchy,
        searchApi: productLineSearchApi,
    } = useGetAllProductLines({
        itemsPerPage: 1000, // Get all product lines to build complete hierarchy
        initSearch: "",
    });
    // Hide the "Range" (productLine) column when the active org has no product
    // line — there is nothing to filter on and no value to render.
    const hasProductLines = productLines.length > 0;

    const { brandCollectionState: brandFilterState, searchApi: brandSearchApi } = brandFilterQuery;
    const { brandCollectionState: brandExistenceState } = brandExistenceQuery;
    const hasBrandQueryError = isBrandCollectionError(brandFilterState);
    const hasBrands = brandExistenceState.status !== "empty" || hasBrandQueryError;
    const brandOptions =
        brandFilterState.status === "success-value" || brandFilterState.status === "empty" ?
            brandFilterState.items
        :   [];

    const allValues = productLinesHierarchy.values().reduce(
        (acc, pLine) => {
            pLine.forEach((i) => {
                acc.push({
                    id: i,
                    name: i,
                });
            });
            return acc;
        },
        [] as { id: string; name: string }[],
    );

    const columnHelper = createMRTColumnHelper<GridProduct>();
    const { getScoresByType, entitiesJsonAttributes } = useFoodpilotContext();

    const noValue = "-";
    const mrt_columns: MRT_ColumnDef<GridProduct, any>[] = [
        columnHelper.accessor("title", {
            grow: false,
            id: "title",
            header: t("nom"),
            Cell: (value) => {
                if (value.row.original.canView) {
                    const productId = value.row.original.id;
                    const currentUrl = location.pathname + location.search;
                    const scoresUrl = URLHandler.product.scores(company.id, productId);
                    const url = `${scoresUrl}?returnUrl=${encodeURIComponent(currentUrl)}`;
                    return <GridLink appearance={appearance} to={url} label={value.renderedCellValue} />;
                }
                return <GridText appearance={appearance} value={value.renderedCellValue}></GridText>;
            },
            Filter: (mrtProps) => {
                return <StringFilter mrtProps={mrtProps} />;
            },
            enableSorting: true,
            enableColumnFilter: true,
        }),
        columnHelper.accessor((row) => row.clientId ?? noValue, {
            id: "clientId",
            header: t("identifiant_unique"),
            Cell: (value) => {
                return <GridText appearance={appearance} value={value.cell.getValue()} />;
            },
            Filter: (mrtProps) => {
                return <StringFilter mrtProps={mrtProps} />;
            },
            enableColumnFilter: true,
            enableSorting: true, // Server-side sorting handled by API Platform OrderFilter
        }),
        ...(hasBrands ?
            [
                columnHelper.accessor((row: GridProduct) => row.brand?.name ?? noValue, {
                    id: "brand.name",
                    header: t("marque"),
                    Cell: (value) => <GridText appearance={appearance} value={value.renderedCellValue} />,
                    Filter: (mrtProps) => {
                        return (
                            <SelectManyFromFilter
                                appearance={appearance}
                                selectOptions={{
                                    kind: "checkbox",
                                    values: brandOptions.map((b) => ({ id: b.id, name: b.name })),
                                    getId: (item) => item.id,
                                    getName: (item) => item.name,
                                }}
                                mrtProps={mrtProps}
                                searchApi={brandSearchApi}
                            />
                        );
                    },
                    enableColumnFilter: true,
                    enableSorting: true,
                }),
            ]
        :   []),
        ...(hasProductLines ?
            [
                columnHelper.accessor(
                    (row: GridProduct) => {
                        if (!row.productLine) return noValue;

                        // Use hierarchyPath if available (from API), otherwise fallback to just name
                        return row.productLine.hierarchyPath || row.productLine.name;
                    },
                    {
                        id: "productLine.name",
                        header: t("gamme"),
                        Cell: (value) => <GridText appearance={appearance} value={value.renderedCellValue} />,
                        Filter: (mrtProps) => {
                            return (
                                <SelectManyFromFilter
                                    appearance={appearance}
                                    selectOptions={{
                                        kind: "checkbox",
                                        values: allValues,
                                        getId: (item) => item.id,
                                        getName: (item) => item.name,
                                    }}
                                    mrtProps={mrtProps}
                                    searchApi={productLineSearchApi}
                                />
                            );
                        },
                        enableColumnFilter: true,
                        enableSorting: true,
                    },
                ),
            ]
        :   []),
    ];

    const productsScores = getScoresByType("product");
    extendColumnsByScores(mrt_columns, data ?? [], productsScores, {
        getReferenceTitle: (scoreTitle) => {
            return t("grid.column.header.reference_score", "{{scoreTitle}} reference", { scoreTitle });
        },
        getComparisonTitle: (scoreTitle) => {
            return t("grid.column.header.comparison_score", "{{scoreTitle}} Δ%", { scoreTitle });
        },
        enableFiltering: true,
        enableSorting: true, // Applies to reference scores
        enableOlapSorting: true, // Server-side sorting via ProductScoreOrderFilter
        FilterComponent: ScoreFilter,
        appearance,
    });

    const productJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.product] || [];
    extendColumnsByJsonAttributes(mrt_columns, productJsonAttributes, data, { appearance });

    extendByColumns(mrt_columns, tagColumns);

    return mrt_columns;
};
